<template>
  <div class="ten-tree-node" :data-level="levelIndex">
    <div
      :class="_contentClass"
      v-on="_contentEvents"
      :draggable="_draggable"
    >
      <div
        :class="_expandClass"
        @click.stop="onExpandClick"
        v-if="!nodeData.isLeaf"
      >
        <ten-icon
          :icon="loading ? 'loading_gradient': 'carret_right'"
        />
      </div>
      <div
        class="ten-tree-node__checkbox"
        v-if="selectable && showCheckbox"
        @click.stop
      >
        <ten-checkbox
          :checked="_selected"
          :disabled="_disabled"
          :value="nodeData.key"
          :indeterminate="_halfSelected"
          @change="onSelect"
        />
      </div>
      <slot>
        <render-cell :render="renderContent" />
      </slot>
    </div>
    <ten-slide-down
      v-if="_children && _children.length > 0"
      :disabled="!tree.inited"
    >
      <div
        class="ten-tree-node__children"
        v-show="!dragging && _expanded"
      >
        <ten-tree-node
          v-for="(item) in nodeData.children"
          :key="tree.getKey(item)"
          :data="item"
          :selectable="selectable"
          :show-checkbox="showCheckbox"
          :level-index="levelIndex + 1"
        />
      </div>
    </ten-slide-down>
  </div>
</template>

<script>
import TenCheckbox from '../checkbox';
import TenIcon from '../icon';
import TenTreeNode from './tree-node';
import TenSlideDown from '../scripts/components/slide-down';
import TreeNodeModel from './tree-node-model';
import RenderCell from '../render-cell';

const name = 'ten-tree-node';
export default {
  name,
  components: {
    TenCheckbox,
    TenIcon,
    TenTreeNode,
    TenSlideDown,
    RenderCell,
  },
  inject: ['tree', 'treeNodeParent'],
  provide() {
    return {
      treeNodeParent: this,
    };
  },
  props: {
    data: {
      type: Object,
      default() {
        return {};
      },
    },
    levelIndex: {
      type: Number,
      default: 0
    },
    selectable: Boolean,
    showCheckbox: Boolean,
  },
  data() {
    return {
      loading: false,
      nodeData: null,
      dragging: false,
      dragoverPos: '', // center top bottom
      status: {
        selected: 0,
        expanded: 0,
      },
    };
  },
  computed: {
    _contentClass() {
      let selected;
      if (this.tree.multiple) {
        selected = this._selected;
      } else {
        selected = this.tree._dataSelected && this.tree._dataSelected.indexOf(this.nodeModel.key) > -1;
      }
      return [
        'ten-tree-node__content',
        {
          'ten-tree-node__content--disabled': this._disabled,
          'ten-tree-node__content--selected': selected,
          'ten-tree-node__content--showcheckbox': this.showCheckbox,
          'ten-tree-node__content--dragging': this.dragging,
          [`ten-tree-node__content--dragpos-${this.dragoverPos}`]: this.dragoverPos,
        },
      ];
    },
    _expandClass() {
      return [
        'ten-tree-node__expand',
        {
          'ten-tree-node__expand--expanded': this._expanded,
        },
      ];
    },
    _contentEvents() {
      const events = {
        click: this.onNodeClick,
        mouseenter: this.onNodeHover,
      };
      if (this._draggable) {
        events.dragstart = this.onDragStart;
        events.dragenter = this.onDragEnter;
        events.dragover = this.onDragOver;
        events.dragleave = this.onDragLeave;
        events.dragend = this.onDragEnd;
        events.drop = this.onDrop;
      }
      return events;
    },
    _children() {
      if (!this.nodeData.isLeaf) {
        return Array.isArray(this.nodeData.children) ? this.nodeData.children : [];
      }
      return [];
    },
    _disabled() {
      return this.data && this.data.disabled;
    },
    _selected() {
      return this.status.selected === 1;
    },
    _halfSelected() {
      return this.status.selected === 2;
    },
    _expanded() {
      return this.status.expanded;
    },
    _draggable() {
      return this.tree.draggable;
    },
  },
  watch: {
    data: {
      handler() {
        const key = this.tree.getKey(this.data);
        const text = this.tree.getText(this.data);
        const children = this.tree.getChildren(this.data);
        const isLeaf = !children;
        const isRemote = !isLeaf && !Array.isArray(children);
        this.nodeModel.setConfig({ key, isLeaf, data: this.data });
        this.nodeModel.setStatus(this.status);
        this.nodeData = {
          key, text, children, isLeaf, isRemote,
        };
      },
      immediate: true,
    },
    _expanded(v) {
      if (v && this.nodeData.isRemote && this.tree.loadChildren) {
        this.loadChildren();
      }
    },
  },
  beforeCreate() {
    this.nodeModel = new TreeNodeModel();
  },
  created() {
    this.nodeModel.mount(this.treeNodeParent.nodeModel);
  },
  beforeDestroy() {
    this.nodeModel.unMount();
  },
  methods: {
    update() {
      const key = this.tree.getKey(this.data);
      const text = this.tree.getText(this.data);
      const children = this.tree.getChildren(this.data);
      const isLeaf = !children;
      const isRemote = !isLeaf && !Array.isArray(children);
      this.nodeModel.setConfig({ key, isLeaf, data: this.data });
      this.nodeData = {
        key, text, children, isLeaf, isRemote,
      };
    },
    renderContent(h) {
      return this.tree.renderTreeNodeContent(h, this.data);
    },
    onSelect() {
      if (this._disabled) {
        return;
      }
      this.tree.onNodeSelectChange(this.nodeData.key, this.status.selected === 1 ? 0 : 1);
    },
    onExpand() {
      if (!this.nodeData.isLeaf && !this._disabled) {
        this.tree.onNodeExpandChange(this.nodeData.key, this.status.expanded === 1 ? 0 : 1);
      }
    },
    loadChildren() {
      if (!this.loading) {
        this.loading = true;
        const p = this.tree.loadChildren(this.data, this.nodeData.key);
        if (typeof p === 'object' && typeof p.then === 'function') {
          p.then((children) => {
            this.loading = false;
            this.tree.setChildren(this.data, children);
            this.update();
          }, () => {
            this.loading = false;
          });
        } else {
          this.loading = false;
        }
      }
    },
    onExpandClick() {
      this.onExpand();
    },
    onNodeClick() {
      if (this._disabled) {
        return;
      }
      if (
        !this.selectable
        || this.showCheckbox
        || (this.tree.selectedMode === 'onlyLeaf' && !this.nodeData.isLeaf)
      ) {
        this.onExpand();
      } else {
        this.onSelect();
      }
      this.tree.$emit('nodeClick', this.nodeModel.key, this.nodeModel.data);
    },
    onNodeHover() {
      this.tree.$emit('nodeHover', this.nodeModel.key, this.nodeModel.data);
    },
    onDragStart(e) {
      const sourceModelId = this.nodeModel.id;
      this.dragging = true;
      e.dataTransfer.setData('ten-tree-node-data', this.nodeModel.id);
      this.tree.draggingSourceModelId = sourceModelId;
      this.tree.onNodeDragStart(sourceModelId, e);
    },
    onDragEnter(e) {
      const sourceModelId = this.tree.draggingSourceModelId;
      const targetModelId = this.nodeModel.id;
      if (sourceModelId === targetModelId) {
        return;
      }
      this.tree.onNodeDragEnter(sourceModelId, targetModelId, e);
    },
    onDragOver(e) {
      e.preventDefault();
      const sourceModelId = this.tree.draggingSourceModelId;
      const targetModelId = this.nodeModel.id;
      if (sourceModelId === targetModelId) {
        return;
      }
      const elemHeight = e.target.offsetHeight;
      const dragY = e.offsetY;
      const insertAreaPercent = 0.3;
      const insertAreaHeight = elemHeight * insertAreaPercent;

      if (dragY < insertAreaHeight) {
        this.dragoverPos = 'top';
      } else if (dragY > elemHeight - insertAreaHeight) {
        this.dragoverPos = 'bottom';
      } else {
        this.dragoverPos = 'center';
      }
      this.tree.onNodeDragOver(sourceModelId, targetModelId, e);
    },
    onDragLeave(e) {
      const sourceModelId = this.tree.draggingSourceModelId;
      const targetModelId = this.nodeModel.id;
      this.dragoverPos = '';
      this.tree.onNodeDragLeave(sourceModelId, targetModelId, e);
    },
    onDragEnd(e) {
      this.dragging = false;
      this.dragoverPos = '';
      this.tree.onNodeDragEnd(this.nodeModel, e);
    },
    onDrop(e) {
      e.preventDefault();
      this.dragging = false;

      const sourceModelId = Number(e.dataTransfer.getData('ten-tree-node-data'));
      const targetModelId = this.nodeModel.id;
      const { dragoverPos } = this;

      this.dragoverPos = '';
      if (sourceModelId === targetModelId) {
        return;
      }
      setTimeout(() => {
        if (dragoverPos !== 'center') {
          this.tree.insert(dragoverPos === 'top' ? 0 : 1, sourceModelId, targetModelId, e);
        } else {
          this.tree.append(sourceModelId, targetModelId, e);
        }
        this.tree.draggingSourceModelId = null;
      }, 0);
      this.tree.onNodeDrop(sourceModelId, targetModelId, e);
    },
  },
};
</script>
